<template>
  <div
    ref="root"
    class="components__cnt-slide_show"
    v-on:contextmenu.prevent
    tabindex="-1"
    v-on:keydown.right="navigate(true)"
    v-on:keydown.left="navigate(false)"
    v-on:wheel.prevent="onWheel"
  >
    <div class="not-authorized" v-if="notAuthorized">
      <i class="frontend-icon fi-fa_lock"></i>
      <i>Auf diesen Inhalt haben Sie keinen Zugriff</i>
    </div>
    <template v-else>
      <div class="slide__navigation">
        <div class="slide__navigation_section">
          <a v-on:click="navigate(false)">Zurück</a
          ><a v-on:click="navigate(true)">Weiter</a>
        </div>
        <div
          class="slide__navigation_section slide__navigation_section--center"
        >
          {{ currentSlidePosition }}
          / {{ totalSlides }}
        </div>
        <div class="slide__navigation_section slide__navigation_section--right">
          <input
            placeholder="zu Seite"
            type="number"
            v-model="inputValue"
            v-on:keyup.enter="navigateAbsolute"
          />
          <i
            title="Vollbild"
            v-on:click="fullscreen"
            v-bind:class="classFullscreenIcon"
          ></i>
        </div>
      </div>
      <div class="slide__image-container">
        <div class="slide__overlay"></div>
        <img
          v-bind:src="currentSlide.image.url"
          v-on:contextmenu.prevent
          v-on:dragstart.prevent
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.components__cnt-slide_show {
  @import "~stylesheets/frontend/variables/colors";

  @media print {
    // hide content in print screen
    display: none;
  }

  &:focus {
    outline: none;
  }

  background-color: $color-black-very-light;

  img {
    max-width: 100%;
    max-height: 800px;

    background-color: white;
  }

  .slide__navigation {
    background-color: $color-black-placeholder;
    color: white;
    padding: 10px 0;

    display: flex;
    align-items: center;

    > div {
      flex-basis: 30%;
      flex-grow: 1;
      flex-shrink: 0;
    }

    .slide__navigation_section {
      display: flex;
      align-items: center;
    }
    .slide__navigation_section--center {
      justify-content: center;
    }
    .slide__navigation_section--right {
      justify-content: flex-end;
    }

    a,
    i {
      margin: 0 10px;
      color: white;
      cursor: pointer;
      user-select: none;
    }

    input {
      width: 60px;
      border: none;
      background-color: $color-black-very-light;
      color: $color-green;
      margin-right: 2px;

      text-align: left;
      &,
      &:focus {
        outline: none;
        border-radius: none;
      }
    }

    // hide arrows on number field
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* Firefox */
    input[type="number"] {
      -moz-appearance: textfield;
    }
  }

  .slide__image-container {
    position: relative;
    text-align: center;
  }

  .slide__overlay {
    // remove drag and drop
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  &:fullscreen {
    img {
      max-height: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .not-authorized {
    display: flex;
    padding: 40px 30px;
    align-items: center;

    .frontend-icon {
      color: $color-green;
      margin-right: 15px;
      font-size: 30px;
    }
  }
}
</style>

<script lang="ts" setup>
  import { computed, onMounted, ref, toRefs } from 'vue';
  import { useStore } from 'vuex';
  import { cntProps } from 'vue/applications/frontend/mixins/cnt-component';

  const props = defineProps({...cntProps()}); 

  const store = useStore();
  const currentSlideIndex = ref<number>(0);
  const isFullscreen = ref<boolean>(false); 
  const root = ref<HTMLElement | null>(null);

  const currentSlidePosition = computed(() => currentSlideIndex.value + 1)
  const totalSlides = computed(() => props.contentItem.widget_data.slide_show.slide_show_slides.length);
  const currentSlide = computed(() => props.contentItem.widget_data.slide_show.slide_show_slides[currentSlideIndex.value])
  const classFullscreenIcon = computed(() => {
    const result: Record<string,boolean> = {
      "frontend-icon": true,
    };
    if (isFullscreen.value) {
      result["fi-fa_compress"] = true;
    } else {
      result["fi-fa_expand"] = true;
    }
    return result;
  });
  const notAuthorized = computed(() => {
    return (
      props.contentItem.widget_data.slide_show.needs_book_license &&
      !store.state.gon.user.has_book_license
    );
  });

  const navigate = (forward: boolean) => {
    if (forward && currentSlideIndex.value < totalSlides.value - 1) {
      currentSlideIndex.value += 1;
    } else if (!forward && currentSlideIndex.value > 0) {
      currentSlideIndex.value -= 1;
    }
  };
  const inputValue = ref(null);
  const navigateAbsolute = (event: InputEvent) => {
    const newValue = parseInt((event.target as HTMLInputElement).value, 10);
    inputValue.value = null; // remove value

    let targetIndex =
      newValue -
      1 +
      (props.contentItem.widget_data.slide_show.slide_position_offset || 0);

    // sanitize new index
    targetIndex = Math.min(targetIndex, totalSlides.value - 1);
    targetIndex = Math.max(targetIndex, 0);

    if (
      targetIndex <
      props.contentItem.widget_data.slide_show.slide_position_offset
    )
      targetIndex = 0;

    currentSlideIndex.value = targetIndex;
  }; 
  const fullscreen = () => {
    if (isFullscreen.value) {
      document.exitFullscreen();
    } else {
      if(root.value)
        root.value.requestFullscreen();
    }
  };
  const setupFullscreenListener = () => {
    document.addEventListener("fullscreenchange", () => {
      isFullscreen.value = document.fullscreenElement === root.value;
    });
  };
  const onWheel = (event: WheelEvent) => {
    if (event.deltaY > 0) {
      navigate(true);
    } else if (event.deltaY < 0) {
      navigate(false);
    }
  };

  onMounted(setupFullscreenListener);
</script>
